import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useFavorites } from '../context/FavoritesContext';
import {
  Map,
  Plus,
  Users,
  Heart,
  TrendingUp,
  MapPin,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { useEffect, useState } from 'react';
import api from '../api/axios';

const Home = () => {
  const { toggleFavorite, isFavorite } = useFavorites();
  const [stats, setStats] = useState({
    totalOffers: 0,
    activeUsers: 0,
    pendingOffers: 0,
    approvedThisMonth: 0
  });
  const [recentOffers, setRecentOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, offersRes] = await Promise.all([
        api.get('/stats'),
        api.get('/offers?status=approved')
      ]);
      setStats(statsRes.data);
      setRecentOffers(offersRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-100 via-rose-100 to-purple-200">
        <Navbar />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-2xl text-gray-600">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-rose-100 to-purple-200">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium animate-fade-in">
              <Sparkles className="w-4 h-4" />
              Plateforme de Solidarité Nouvelle Génération
            </div>

            <h1 className="text-5xl md:text-7xl font-bold">
              <span className="bg-gradient-to-r from-purple-600 via-red-500 to-yellow-500 bg-clip-text text-transparent animate-gradient">
                Ensemble, Construisons
              </span>
              <br />
              <span className="text-slate-800">Un Monde Solidaire</span>
            </h1>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Découvrez et partagez des initiatives solidaires près de chez vous.
              Alimentaire, éducation, santé, loisirs... Rejoignez notre communauté !
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/carte">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 via-red-500 to-yellow-500 hover:from-purple-700 hover:via-red-600 hover:to-yellow-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  <Map className="w-5 h-5 mr-2" />
                  Explorer la Carte
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/ajouter">
                <Button
                  size="lg"
                  variant="outline"
                  className="px-8 py-6 text-lg rounded-xl border-2 border-purple-500 hover:bg-purple-50 transform hover:scale-105 transition-all duration-300"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Proposer une Offre
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <MapPin className="w-6 h-6" />
                  {stats.totalOffers}
                </CardTitle>
                <CardDescription className="text-purple-100">Offres Solidaires</CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Users className="w-6 h-6" />
                  {stats.activeUsers}
                </CardTitle>
                <CardDescription className="text-green-100">Utilisateurs Actifs</CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Heart className="w-6 h-6" />
                  {stats.approvedThisMonth}
                </CardTitle>
                <CardDescription className="text-red-100">Ce Mois-ci</CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 bg-gradient-to-br from-yellow-500 to-yellow-600 text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <TrendingUp className="w-6 h-6" />
                  +24%
                </CardTitle>
                <CardDescription className="text-yellow-100">Croissance</CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Recent Offers */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Dernières Offres Solidaires
            </h2>
            <p className="text-gray-600 text-lg">
              Découvrez les initiatives récemment ajoutées par notre communauté
            </p>
          </div>

          {recentOffers.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {recentOffers.map((offer) => (
                  <Card
                    key={offer.id}
                    className="group overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-none"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={offer.image || 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=800'}
                        alt={offer.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            toggleFavorite(offer.id);
                          }}
                          className="bg-white/90 hover:bg-white p-2 rounded-full shadow-lg hover:scale-110 transition-transform"
                        >
                          <Heart
                            className={`w-5 h-5 ${
                              isFavorite(offer.id)
                                ? 'fill-red-500 text-red-500'
                                : 'text-gray-600 hover:text-red-500'
                            }`}
                          />
                        </button>
                      </div>
                      <div className="absolute top-4 right-4">
                        <Badge className="bg-purple-500 text-white">
                          {offer.subcategory}
                        </Badge>
                      </div>
                    </div>
                    <CardHeader>
                      <CardTitle className="group-hover:text-purple-600 transition-colors">
                        {offer.title}
                      </CardTitle>
                      <CardDescription className="line-clamp-2">
                        {offer.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                        <MapPin className="w-4 h-4" />
                        {offer.address}
                      </div>
                      <Link to={`/offre/${offer.id}`}>
                        <Button className="w-full bg-gradient-to-r from-purple-600 to-red-500 hover:from-purple-700 hover:to-red-600">
                          Voir Détails
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="text-center mt-12">
                <Link to="/carte">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-purple-500 hover:bg-purple-50"
                  >
                    Voir Toutes les Offres
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <p className="text-lg">Aucune offre disponible pour le moment.</p>
              <Link to="/ajouter">
                <Button className="mt-4 bg-gradient-to-r from-purple-600 to-red-500">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter la première offre
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;